import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  type SyntheticEvent,
} from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Tabs,
  Tab,
  Dialog,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { AgGridReact, AgGridProvider } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  ValidationModule,
  type RowClassParams,
  type RowClickedEvent,
} from "ag-grid-community";
import { apiGet, apiPost } from "../../services/genesisApi/httpsMethods";
import { updateDealComps } from "../../services/dealCompsApi";
import { useReferenceLists } from "../../context/GlobalGetLists";
import "../../assets/css/AGGrid-custom.css";
import "../../assets/css/tabs.css";
import DealCompComparisonMap, {
  type DealCompMapEntry,
} from "./DealCompComparisonMap";
import type {
  DealCompsAnalysis,
  DealCompsPayload,
  LeaseComp,
  LoanCompFromApi,
  SalesComp,
  DealCompsSectionProps,
} from "./DealCompSectionTypes";
import {
  COMPS_GRID_MIN_HEIGHT_PX,
  COMPS_MAP_HEIGHT_PX,
  COMPS_ROW_MIN_HEIGHT_PX,
} from "./DealCompSectionConstants";
import {
  buildCompMapEntriesForTab,
  collectSortedUniqueSources,
  normalizeLeaseComps,
  normalizeLoanComps,
  normalizeSalesComps,
  resolveCompsAnalysisFromApiData,
  resolveDealCompsPayloadFromApiData,
} from "./DealCompSectionModel";
import {
  buildLeaseColumns,
  buildLoanColumns,
  buildSalesColumns,
  dealCompSourceCellRenderer,
} from "./DealCompSectionColumns";
import { DealCompSectionTabPanel } from "./DealCompSectionTabPanel";
import { CompAnalysis } from "./CompAnalysis";
import DealCompFilter, { type DealCompsQueryParams } from "./DealCompFilter";
import "@/assets/css/tabs.css";
import { useAccordionFilteredMapAssets } from "@/hooks/useAccordionFilteredMapAssets";
import { useAssetMap, useAssetMapSettings } from "@/hooks/useAssetMap";
import { salesCompsAreaUnitLabel } from "@/utils/dealAreaUnit";
import { isSalesCompSourceId } from "@/constants/salesCompSources";

/** Keys on `GET deal/:id/comps/filters` `data.filters` that map to comps query params. */
const SAVED_COMPS_FILTER_KEYS_FOR_QUERY = [
  "radius_km",
  "months_back",
  "loan_amount_min",
  "loan_amount_max",
  "sales_price_min",
  "sales_price_max",
  "lease_total_rent_min",
  "lease_total_rent_max",
  "lease_area_min",
  "lease_area_max",
  "limit_per_type",
  "sales_area_min",
  "sales_area_max",
  "sales_require_yield",
  "sales_sources",
  "sales_vendor",
  "sales_purchaser",
  "show_validated",
] as const;

const SAVED_COMPS_FILTER_KEY_SET = new Set<string>(
  SAVED_COMPS_FILTER_KEYS_FOR_QUERY,
);

/** Keys on filters object that are not persisted filter fields. */
const SAVED_COMPS_FILTER_METADATA_KEYS = new Set([
  "updated_at",
  "deal_id",
  "saved",
]);

function coerceSavedFilterScalar(value: unknown): string | number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const t = value.trim();
    if (t === "") return undefined;
    const n = Number(t);
    if (Number.isFinite(n)) return n;
    return t;
  }
  return undefined;
}

function coerceSavedFilterString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== "string") return undefined;
  const t = value.trim();
  return t === "" ? undefined : t;
}

function coerceSavedFilterSalesSources(value: unknown): string[] | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const ids = value.filter(
    (item): item is string =>
      typeof item === "string" && isSalesCompSourceId(item),
  );
  return ids.length > 0 ? ids : undefined;
}

function savedCompsApiFiltersToQueryParams(filters: unknown): {
  params: DealCompsQueryParams;
  unknownKeys: string[];
} {
  if (
    filters === null ||
    filters === undefined ||
    typeof filters !== "object"
  ) {
    return { params: {}, unknownKeys: [] };
  }
  const rec = filters as Record<string, unknown>;
  const unknownKeys = Object.keys(rec).filter(
    (k) =>
      !SAVED_COMPS_FILTER_KEY_SET.has(k) &&
      !SAVED_COMPS_FILTER_METADATA_KEYS.has(k),
  );

  const params: DealCompsQueryParams = {};
  for (const key of SAVED_COMPS_FILTER_KEYS_FOR_QUERY) {
    if (key === "sales_require_yield") {
      if (rec[key] === true) params.sales_require_yield = true;
      continue;
    }
    if (key === "show_validated") {
      if (rec[key] === false) params.show_validated = false;
      else if (rec[key] === true) params.show_validated = true;
      continue;
    }
    if (key === "sales_sources") {
      const sources = coerceSavedFilterSalesSources(rec[key]);
      if (sources !== undefined) params.sales_sources = sources;
      continue;
    }
    if (key === "sales_vendor" || key === "sales_purchaser") {
      const s = coerceSavedFilterString(rec[key]);
      if (s !== undefined) params[key] = s;
      continue;
    }
    const v = coerceSavedFilterScalar(rec[key]);
    if (v !== undefined) params[key] = v;
  }
  return { params, unknownKeys };
}

/** Body for POST `deal/:id/comps/filters` when clearing all comps filters. */
const CLEARED_COMPS_FILTERS_BODY = {
  radius_km: null,
  months_back: null,
  loan_amount_min: null,
  loan_amount_max: null,
  sales_price_min: null,
  sales_price_max: null,
  lease_total_rent_min: null,
  lease_total_rent_max: null,
  lease_area_min: null,
  lease_area_max: null,
  limit_per_type: null,
  sales_area_min: null,
  sales_area_max: null,
  sales_require_yield: null,
  sales_sources: null,
  sales_vendor: null,
  sales_purchaser: null,
  show_validated: true,
  updated_at: "2026-04-16T15:14:18.806943+00:00",
} as const;

const DealCompsSection: React.FC<DealCompsSectionProps> = ({
  dealId,
  assets,
  accordions,
  countries,
}) => {
  const { users } = useReferenceLists();
  const [payload, setPayload] = useState<DealCompsPayload | null>(null);
  const [compsAnalysis, setCompsAnalysis] = useState<DealCompsAnalysis | null>(
    null,
  );

  const [leaseData, setLeaseData] = useState<LeaseComp[]>([]);
  const [loanData, setLoanData] = useState<LoanCompFromApi[]>([]);
  const [salesData, setSalesData] = useState<SalesComp[]>([]);

  const [sourceNames, setSourceNames] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  /**
   * Index of the clicked comp in the active tab's data array (and therefore in
   * `compMapEntries`). Not the grid's displayed row index, which changes when
   * the grid is sorted.
   */
  const [focusedCompEntryIndex, setFocusedCompEntryIndex] = useState<
    number | null
  >(null);
  /** Bumps on every grid row click so the map re-focuses the same row. */
  const [compMapFocusSeq, setCompMapFocusSeq] = useState(0);
  const [compMapEntries, setCompMapEntries] = useState<DealCompMapEntry[]>([]);
  const [compMapFullscreenOpen, setCompMapFullscreenOpen] = useState(false);
  const [compsQueryParams, setCompsQueryParams] =
    useState<DealCompsQueryParams>({});
  const [savedCompsFiltersLoaded, setSavedCompsFiltersLoaded] = useState(false);
  const [clearFiltersRequested, setClearFiltersRequested] = useState(false);
  const clearingCompsFiltersRef = useRef(false);
  /** After a successful "clear filters" POST, ignore one debounced persist (empty params echo). */
  const skipNextDebouncedCompsPersistRef = useRef(false);

  useEffect(() => {
    setCompMapEntries(
      buildCompMapEntriesForTab(activeTab, leaseData, salesData, loanData),
    );
    setFocusedCompEntryIndex(null);
    setCompMapFocusSeq(0);
  }, [activeTab, leaseData, salesData, loanData]);

  useEffect(() => {
    if (!dealId) return;

    let isMounted = true;

    const fetchCompFilters = async () => {
      if (isMounted) setSavedCompsFiltersLoaded(false);
      try {
        const response = await apiGet(`deal/${dealId}/comps/filters`);
        if (!isMounted) return;

        if (!response?.success || response.data == null) return;

        const body = response.data as { filters?: unknown };
        if (!("filters" in body)) return;

        const { params, unknownKeys } = savedCompsApiFiltersToQueryParams(
          body.filters,
        );
        if (unknownKeys.length > 0) {
          console.error(
            "[DealCompsSection] GET deal/…/comps/filters returned unknown filter keys (not mapped to comps query params):",
            unknownKeys,
          );
        }
        setCompsQueryParams(params);
      } catch (e) {
        if (isMounted) console.error(e);
      } finally {
        if (isMounted) setSavedCompsFiltersLoaded(true);
      }
    };

    void fetchCompFilters();

    return () => {
      isMounted = false;
    };
  }, [dealId]);

  const { filteredAssets } = useAccordionFilteredMapAssets(
    assets,
    accordions,
    undefined,
  );

  const { coordinates } = useAssetMap(filteredAssets);
  const { center } = useAssetMapSettings(coordinates);

  const salesAreaUnitLabel = useMemo(
    () => salesCompsAreaUnitLabel(countries),
    [countries],
  );

  const fetchComps = useCallback(
    async (isMounted: () => boolean) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiGet(`deal/${dealId}/comps`);

        if (!isMounted()) return;

        if (response && response.success && response.data) {
          const data = resolveDealCompsPayloadFromApiData(response.data);
          if (!data) {
            setCompsAnalysis(null);
            return;
          }

          setCompsAnalysis(resolveCompsAnalysisFromApiData(response.data));

          const sourceNames = collectSortedUniqueSources(data);

          if (sourceNames.length > 0) {
            setSourceNames(sourceNames);
          }
          setPayload(data);
          setLeaseData(normalizeLeaseComps(data?.lease_comps || []));
          setLoanData(normalizeLoanComps(data?.loan_comps || []));
          setSalesData(normalizeSalesComps(data?.sales_comps || []));
        }
      } catch (e) {
        if (!isMounted()) return;
        setCompsAnalysis(null);
        setError("Unable to load comps.");
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [dealId],
  );

  /** One comps GET (with shared filter query) loads lease, sales, and loan rows for every tab. */
  useEffect(() => {
    let isMounted = true;

    if (dealId) void fetchComps(() => isMounted);

    return () => {
      isMounted = false;
    };
  }, [dealId, fetchComps]);

  const handleTabChange = useCallback(
    (_event: SyntheticEvent, newTab: number) => {
      if (newTab === activeTab) return;
      setActiveTab(newTab);
    },
    [activeTab],
  );

  const activeTabRows = useMemo<(LeaseComp | SalesComp | LoanCompFromApi)[]>(
    () => (activeTab === 0 ? leaseData : activeTab === 1 ? salesData : loanData),
    [activeTab, leaseData, salesData, loanData],
  );

  const handleCompRowClicked = useCallback(
    (event: RowClickedEvent) => {
      const data = event.data as
        | LeaseComp
        | SalesComp
        | LoanCompFromApi
        | undefined;
      if (!data) return;
      // Resolve the clicked comp in the unsorted data (same order as
      // compMapEntries): by object identity first, then by comp id.
      let index = activeTabRows.indexOf(data);
      if (index < 0 && data.id != null) {
        index = activeTabRows.findIndex((row) => row.id === data.id);
      }
      if (index < 0) return;
      setFocusedCompEntryIndex(index);
      setCompMapFocusSeq((s) => s + 1);
    },
    [activeTabRows],
  );

  const focusedCompRow =
    focusedCompEntryIndex != null
      ? (activeTabRows[focusedCompEntryIndex] ?? null)
      : null;

  const persistCompsFiltersAndRefresh = useCallback(
    async (body: object) => {
      if (!dealId) return;

      setError(null);
      setLoading(true);
      try {
        const response = await apiPost(`deal/${dealId}/comps/filters`, body);
        if (!response.success) {
          setError(
            typeof response.message === "string"
              ? response.message
              : "Unable to update comps.",
          );
          setLoading(false);
          return;
        }
        if (body === CLEARED_COMPS_FILTERS_BODY) {
          skipNextDebouncedCompsPersistRef.current = true;
        }
        await fetchComps(() => true);
      } catch {
        setError("Unable to update comps.");
        setLoading(false);
      }
    },
    [dealId, fetchComps],
  );

  const handleDebouncedCompsFilterParamsChange = useCallback(
    async (params: DealCompsQueryParams, hasValidationErrors: boolean) => {
      if (!savedCompsFiltersLoaded) return;

      const next = {
        ...params,
        show_validated: compsQueryParams.show_validated !== false,
      };
      setCompsQueryParams(next);

      if (skipNextDebouncedCompsPersistRef.current) {
        skipNextDebouncedCompsPersistRef.current = false;
        return;
      }

      if (clearingCompsFiltersRef.current) return;
      if (!dealId || hasValidationErrors) return;

      await persistCompsFiltersAndRefresh(next);
    },
    [
      dealId,
      savedCompsFiltersLoaded,
      persistCompsFiltersAndRefresh,
      compsQueryParams.show_validated,
    ],
  );

  const handleToggleValidatedChange = useCallback(
    (show: boolean) => {
      if (!savedCompsFiltersLoaded) return;
      const next = { ...compsQueryParams, show_validated: show };
      setCompsQueryParams(next);
      void persistCompsFiltersAndRefresh(next);
    },
    [savedCompsFiltersLoaded, compsQueryParams, persistCompsFiltersAndRefresh],
  );

  const handleManuallyValidatedChange = useCallback(
    async (
      kind: "lease" | "sales" | "loan",
      row: { id?: number },
      checked: boolean,
    ) => {
      if (row.id == null || !dealId) return;

      const auditFields = checked
        ? {
            manually_validated: true as const,
            manually_validated_at: new Date().toISOString(),
          }
        : {
            manually_validated: false as const,
            manually_validated_at: null,
            manually_validated_by_user_id: null,
          };

      const patchRows = <T extends { id?: number }>(rows: T[]) =>
        rows.map((item) =>
          item.id === row.id ? { ...item, ...auditFields } : item,
        );
      const dropRow = <T extends { id?: number }>(rows: T[]) =>
        rows.filter((item) => item.id !== row.id);

      const patchedLease = kind === "lease" ? patchRows(leaseData) : leaseData;
      const patchedLoan = kind === "loan" ? patchRows(loanData) : loanData;
      const patchedSales = kind === "sales" ? patchRows(salesData) : salesData;

      const showValidated = compsQueryParams.show_validated !== false;
      const removeFromTable = !checked && showValidated;

      if (kind === "lease") {
        setLeaseData(removeFromTable ? dropRow(leaseData) : patchedLease);
      }
      if (kind === "sales") {
        setSalesData(removeFromTable ? dropRow(salesData) : patchedSales);
      }
      if (kind === "loan") {
        setLoanData(removeFromTable ? dropRow(loanData) : patchedLoan);
      }

      setError(null);

      try {
        const response = await updateDealComps(dealId, {
          lease_comps: patchedLease,
          loan_comps: patchedLoan,
          sales_comps: patchedSales,
        });

        if (!response.success) {
          if (kind === "lease") setLeaseData(leaseData);
          if (kind === "sales") setSalesData(salesData);
          if (kind === "loan") setLoanData(loanData);
          setError(
            typeof response.message === "string"
              ? response.message
              : "Unable to update validation.",
          );
          return;
        }

        // Only refetch when "Validated" filter is on (row may leave the list).
        if (showValidated) {
          await fetchComps(() => true);
          if (removeFromTable) {
            if (kind === "lease") setLeaseData((rows) => dropRow(rows));
            if (kind === "sales") setSalesData((rows) => dropRow(rows));
            if (kind === "loan") setLoanData((rows) => dropRow(rows));
          }
        }
      } catch {
        if (kind === "lease") setLeaseData(leaseData);
        if (kind === "sales") setSalesData(salesData);
        if (kind === "loan") setLoanData(loanData);
        setError("Unable to update validation.");
      }
    },
    [
      dealId,
      leaseData,
      loanData,
      salesData,
      compsQueryParams.show_validated,
      fetchComps,
    ],
  );

  const handleClearFiltersClick = useCallback(() => {
    void (async () => {
      clearingCompsFiltersRef.current = true;
      setClearFiltersRequested(true);
      try {
        await persistCompsFiltersAndRefresh(CLEARED_COMPS_FILTERS_BODY);
      } finally {
        clearingCompsFiltersRef.current = false;
      }
    })();
  }, [persistCompsFiltersAndRefresh]);

  const renderSourceLink = useCallback(
    (params: any) => dealCompSourceCellRenderer(params, sourceNames),
    [sourceNames],
  );

  const resolveValidationUserName = useCallback(
    (userId: number) => {
      const match = (Array.isArray(users) ? users : []).find(
        (user) => user.id === userId,
      );
      return typeof match?.name === "string" && match.name.trim()
        ? match.name.trim()
        : null;
    },
    [users],
  );

  const leaseColumns = useMemo(
    () =>
      buildLeaseColumns(
        renderSourceLink,
        (row, checked) => handleManuallyValidatedChange("lease", row, checked),
        resolveValidationUserName,
      ),
    [renderSourceLink, handleManuallyValidatedChange, resolveValidationUserName],
  );
  const loanColumns = useMemo(
    () =>
      buildLoanColumns(
        renderSourceLink,
        (row, checked) => handleManuallyValidatedChange("loan", row, checked),
        resolveValidationUserName,
      ),
    [renderSourceLink, handleManuallyValidatedChange, resolveValidationUserName],
  );
  const salesColumns = useMemo(
    () =>
      buildSalesColumns(
        renderSourceLink,
        (row, checked) => handleManuallyValidatedChange("sales", row, checked),
        resolveValidationUserName,
      ),
    [renderSourceLink, handleManuallyValidatedChange, resolveValidationUserName],
  );

  const modulesMemo = useMemo(
    () => [
      ClientSideRowModelModule,
      ...(process.env.NODE_ENV !== "production" ? [ValidationModule] : []),
    ],
    [],
  );

  /** Same pixel width for every comps column (lease / sales / loans). */
  const compsGridDefaultColDef = useMemo(
    () => ({
      width: 150,
      resizable: true,
      sortable: true,
    }),
    [],
  );

  const compsGridRowClassRules = useMemo(
    () => ({
      "deal-comps-grid-row--focused": (params: RowClassParams) =>
        focusedCompRow != null && params.data === focusedCompRow,
    }),
    [focusedCompRow],
  );

  const renderCompsTablePanel = (isFullscreen: boolean) => {
    const gridBoxSx = isFullscreen
      ? {
          flex: 1,
          minHeight: 0,
          width: "100%",
          minWidth: 0,
          height: "100%",
        }
      : {
          flex: 1,
          minHeight: COMPS_GRID_MIN_HEIGHT_PX,
          width: "100%",
          minWidth: 0,
        };
    const tabVariant = isFullscreen ? ("fill" as const) : ("default" as const);

    return (
      <Paper
        sx={{
          flex: "1 1 60%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          ...(isFullscreen
            ? { minHeight: 0, height: "100%" }
            : { minHeight: COMPS_ROW_MIN_HEIGHT_PX }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
            pr: 1,
            gap: 1,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            TabIndicatorProps={{ className: "tf-tabs__indicator" }}
            sx={{
              minHeight: 0,
              width: "auto",
              "& .MuiTabs-flexContainer": { alignItems: "center" },
              "& .MuiTab-root.tf-tab.tf-tab--compact": {
                minHeight: "43px !important",
                py: 1,
              },
            }}
          >
            <Tab label="Lease" className="tf-tab tf-tab--compact" />
            <Tab label="Sales" className="tf-tab tf-tab--compact" />
            <Tab label="Loans" className="tf-tab tf-tab--compact" />
          </Tabs>
          <FormControlLabel
            control={
              <Checkbox
                disableRipple
                checked={compsQueryParams.show_validated !== false}
                onChange={(e) => handleToggleValidatedChange(e.target.checked)}
                sx={{ color: "#1B7A63 !important", p: 0, mr: 1 }}
              />
            }
            label="Toggle Validated"
            sx={{
              m: 0,
              alignItems: "center",
              flexShrink: 0,
              whiteSpace: "nowrap",
              "& .MuiFormControlLabel-label": {
                fontSize: "14px !important",
                fontWeight: 500,
                lineHeight: 1.25,
                fontFamily: "Helvetica Neue, sans-serif",
                color: "#182737",
              },
            }}
          />
        </Box>

        <Box
          sx={{
            p: isFullscreen ? 1 : 2,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <DealCompSectionTabPanel
            value={activeTab}
            index={0}
            variant={tabVariant}
          >
            <Box className="ag-theme-alpine deal-comps-grid" sx={gridBoxSx}>
              <AgGridReact
                rowData={leaseData}
                columnDefs={leaseColumns}
                defaultColDef={compsGridDefaultColDef}
                rowClassRules={compsGridRowClassRules}
                rowHeight={30}
                onRowClicked={handleCompRowClicked}
              />
            </Box>
          </DealCompSectionTabPanel>

          <DealCompSectionTabPanel
            value={activeTab}
            index={1}
            variant={tabVariant}
          >
            <Box className="ag-theme-alpine deal-comps-grid" sx={gridBoxSx}>
              <AgGridReact
                rowData={salesData}
                columnDefs={salesColumns}
                defaultColDef={compsGridDefaultColDef}
                rowClassRules={compsGridRowClassRules}
                rowHeight={30}
                onRowClicked={handleCompRowClicked}
              />
            </Box>
          </DealCompSectionTabPanel>

          <DealCompSectionTabPanel
            value={activeTab}
            index={2}
            variant={tabVariant}
          >
            <Box className="ag-theme-alpine deal-comps-grid" sx={gridBoxSx}>
              <AgGridReact
                rowData={loanData}
                columnDefs={loanColumns}
                defaultColDef={compsGridDefaultColDef}
                rowClassRules={compsGridRowClassRules}
                rowHeight={30}
                onRowClicked={handleCompRowClicked}
              />
            </Box>
          </DealCompSectionTabPanel>
        </Box>
      </Paper>
    );
  };

  return (
    <AgGridProvider modules={modulesMemo}>
      <Box sx={{ mt: 0 }}>
        {error ? (
          <Typography color="error" role="alert" sx={{ mb: 1 }}>
            {error}
          </Typography>
        ) : null}
        {loading && !payload && <CircularProgress />}
        {payload && !compMapFullscreenOpen && (
          <Box
            sx={{
              mt: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              alignItems: "stretch",
            }}
          >
            <Box sx={{ width: "100%", boxSizing: "border-box" }}>
              <DealCompFilter
                key={`deal-${dealId}-comps-filters-${savedCompsFiltersLoaded ? "ready" : "boot"}`}
                queryParams={compsQueryParams}
                salesAreaUnitLabel={salesAreaUnitLabel}
                onParamsChange={handleDebouncedCompsFilterParamsChange}
                clearFiltersRequested={clearFiltersRequested}
                setClearFiltersRequested={setClearFiltersRequested}
                onClearFiltersClick={handleClearFiltersClick}
              />
            </Box>

            <Box
              sx={{
                width: "100%",
                boxSizing: "border-box",
                overflow: "visible",
              }}
            >
              <CompAnalysis
                analysis={compsAnalysis}
                compsAverageLoading={loading && payload !== null}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                alignItems: "stretch",
                minHeight: COMPS_ROW_MIN_HEIGHT_PX,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  flex: "1 1 40%",
                  minWidth: 0,
                  minHeight: COMPS_ROW_MIN_HEIGHT_PX,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  position: "relative",
                }}
              >
                <DealCompComparisonMap
                  compMapEntries={compMapEntries}
                  compMapPopupVariant={
                    activeTab === 0
                      ? "lease"
                      : activeTab === 1
                        ? "sales"
                        : "loan"
                  }
                  height={COMPS_MAP_HEIGHT_PX}
                  width="100%"
                  selectionResetKey={activeTab}
                  focusedCompEntryIndex={focusedCompEntryIndex}
                  compMapFocusSeq={compMapFocusSeq}
                  setMapFullscreenOpen={setCompMapFullscreenOpen}
                  mapFullscreenOpen={compMapFullscreenOpen}
                  sourceNames={sourceNames}
                  coordinates={coordinates}
                  center={center}
                  useAssetNumbers={true}
                />
              </Box>

              {renderCompsTablePanel(false)}
            </Box>
          </Box>
        )}
      </Box>

      <Dialog
        fullScreen
        open={compMapFullscreenOpen}
        onClose={() => setCompMapFullscreenOpen(false)}
      >
        {compMapFullscreenOpen && payload ? (
          <Box
            sx={{
              height: "100vh",
              width: "100%",
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch",
              gap: 2,
              boxSizing: "border-box",
              p: 2,
            }}
          >
            <Box
              sx={{
                flex: "1 1 40%",
                minWidth: 0,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  position: "relative",
                  width: "100%",
                }}
              >
                <DealCompComparisonMap
                  key="deal-comp-map-fullscreen"
                  compMapEntries={compMapEntries}
                  compMapPopupVariant={
                    activeTab === 0
                      ? "lease"
                      : activeTab === 1
                        ? "sales"
                        : "loan"
                  }
                  height="100%"
                  width="100%"
                  selectionResetKey={activeTab}
                  focusedCompEntryIndex={focusedCompEntryIndex}
                  compMapFocusSeq={compMapFocusSeq}
                  setMapFullscreenOpen={setCompMapFullscreenOpen}
                  mapFullscreenOpen={compMapFullscreenOpen}
                  sourceNames={sourceNames}
                  coordinates={coordinates}
                  center={center}
                  useAssetNumbers={true}
                />
              </Box>
            </Box>

            {renderCompsTablePanel(true)}
          </Box>
        ) : null}
      </Dialog>
    </AgGridProvider>
  );
};

export default DealCompsSection;
